import type {} from "@bestowinc/tinkertoy-core";
import { z } from "zod";

const serializedNewRelicInitSchema = z.object({
  distributed_tracing: z.object({
    enabled: z.boolean(),
  }),
  privacy: z.object({
    cookies_enabled: z.boolean(),
  }),
});

const serializedNewRelicInfoSchema = z.object({
  beacon: z.string(),
  errorBeacon: z.string(),
  licenseKey: z.string(),
  applicationID: z.string(),
  sa: z.number(),
});

const serializedNewRelicLoaderConfigSchema = z.object({
  accountID: z.string(),
  trustKey: z.string(),
  agentID: z.string(),
  licenseKey: z.string(),
  applicationID: z.string(),
});

const serializedNewRelicConfigSchema = z.object({
  init: serializedNewRelicInitSchema,
  info: serializedNewRelicInfoSchema,
  loader_config: serializedNewRelicLoaderConfigSchema,
});

const serializedLogRocketConfigSchema = z.object({
  projectId: z.string(),
});

const browserTelemetryVendorFields = {
  newRelic: serializedNewRelicConfigSchema,
  logRocket: serializedLogRocketConfigSchema,
};

/** Strict contract schema — host builders must satisfy this shape. */
export const serializedBrowserTelemetryConfigSchema = z.object({
  newRelic: browserTelemetryVendorFields.newRelic.optional(),
  logRocket: browserTelemetryVendorFields.logRocket.optional(),
});

export type SerializedBrowserTelemetryConfig = z.infer<
  typeof serializedBrowserTelemetryConfigSchema
>;

declare module "@bestowinc/tinkertoy-core" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type -- module augmentation merges vendor fields onto the core target
  interface BrowserTelemetryConfig extends SerializedBrowserTelemetryConfig {}
}

/**
 * Lenient wire-field schema: same shape as {@link serializedBrowserTelemetryConfigSchema}
 * with `.optional().catch(undefined)` on each vendor. Invalid vendor values become
 * `undefined` instead of failing the parse; non-object wire values become `undefined`.
 * Collapses to `undefined` when every vendor is absent.
 */
export const shellConfigTelemetryFieldSchema = z
  .object({
    newRelic: browserTelemetryVendorFields.newRelic.optional().catch(undefined),
    logRocket: browserTelemetryVendorFields.logRocket.optional().catch(undefined),
  })
  .transform(telemetry => {
    const { newRelic, logRocket } = telemetry;

    if (newRelic === undefined && logRocket === undefined) {
      return undefined;
    }

    return {
      ...(newRelic !== undefined ? { newRelic } : {}),
      ...(logRocket !== undefined ? { logRocket } : {}),
    };
  })
  .optional()
  .catch(undefined);

type LenientTelemetryField = NonNullable<
  z.infer<typeof shellConfigTelemetryFieldSchema>
>;

/** Host builder output must satisfy the lenient wire parser's successful shape. */
type AssertStrictTelemetryExtendsLenient = SerializedBrowserTelemetryConfig extends LenientTelemetryField
  ? true
  : never;

const _assertStrictTelemetryExtendsLenient: AssertStrictTelemetryExtendsLenient = true;

void _assertStrictTelemetryExtendsLenient;
